import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedTime, injectIntl, useIntl } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroller';
import moment from 'moment';

import api from '@/shared/api';
import SimplePanel from '@/shared/components/SimplePanel';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import DocumentService from '@/shared/services/DocumentService';
import DataCacheService from '@/shared/services/DataCacheService';
import { formatListValue } from '@/shared/components/FormattedListValue';
import handleHttpError from '@/shared/services/HandleHttpError';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import { getInvoiceStreamNamesBulk } from '@/shared/services/InvoiceStreamService';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import ToastrService from '@/shared/services/ToastrService';
import { injectStateService } from '../../../shared/services/StateService';
import config from '../../../config';
import FieldChange from './components/FieldChange';
import EnumChange from './components/EnumChange';
import RelationLinkChange from './components/RelationLinkChange';
import UboLine from './components/UboLine';
import AddressChange from './components/AddressChange';
import AddressSettingChange from './components/AddressSettingChange';
import RelationLinkProperty from './components/RelationLinkProperty';
import RelationCreated from './components/RelationCreated';
import UboEnteredOnline from './components/UboEnteredOnline';
import messages from './components/FieldMessages';
import RelationSummary from '../../interactions/RelationSummary';
import AuthorizationChange from './components/AuthorizationChange';
import AuthorizationPropertyChange from './components/AuthorizationPropertyChange';
import Compliance from './components/Compliance';
import BankAccountChange from './components/BankAccountChange';
import BankAccountPropertyChange from './components/BankAccountPropertyChange';
import dateFormatService from '../../../shared/services/DateFormatService';
import InvoiceStreamChange from './components/InvoiceStreamChange';
import InvoiceStreamPropertyChange from './components/InvoiceStreamPropertyChange';

const onlyUnique = (value, index, self) => self.indexOf(value) === index;
const pageSize = 50;

const RelationAuditContainer = props => {
  const intl = useIntl();
  const { relationId } = props;

  const [hasError, setHasError] = useState(false);
  const [loadingTemplates, setLoadingData] = useState(true);
  const [salutations, setSalutations] = useState({});
  const [templates, setTemplates] = useState({});
  const [invoiceSchedules, setInvoiceSchedules] = useState({});
  const [priceGrids, setPriceGrids] = useState({});
  const [allLogs, setLogs] = useState([]);
  const [ownCompanyIds, setOwnCompanyIds] = useState({});
  const [relationIds, setRelationIds] = useState([]);
  const [usersDictionary, setUsersDictionary] = useState({});
  const [relationsDictionary, setRelationsDictionary] = useState({});
  const [emailTemplates, setEmailTemplates] = useState({});
  const [invoiceStreams, setInvoiceStreams] = useState({}); // For via info
  const [loading, setLoading] = useState(false);
  const [relation, setRelation] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const loadRelationSummary = () => {
      if (!relationId) {
        setHasError(true);
        return;
      }
      api.base
        .get(`${config.appUrls.api}/api/relation/summary?id=${relationId}`)
        .then(({ data }) => {
          if (!data.eidDocumentPhotoId) return data;
          return DocumentService.getLink(data.eidDocumentPhotoId).then(photoUrl => ({
            ...data,
            eidPhotoUrl: photoUrl,
          }));
        })
        .then(setRelation)
        .catch(error => {
          setHasError(true);
          handleHttpError(intl)(error);
        });
    };
    setLogs([]);
    loadRelationSummary();
  }, [relationId, intl]);

  useEffect(() => {
    const newUsersDictionary = DataCacheService.getAllUsers().reduce(
      (prev, current) => ({ ...prev, [current.id]: current.name }),
      {},
    );

    setUsersDictionary(newUsersDictionary);
  }, []);

  useEffect(() => {
    if (relationIds.length === 0) return;

    const uri = `${config.appUrls.api}/api/Relation/Names`;

    const fetchRelations = () => {
      Promise.all([
        api.base.post(uri, relationIds).then(({ data }) => data || []),
        getInvoiceStreamNamesBulk(relationIds),
      ]).then(([data, streamsPerRelation]) => {
        const newRelationsDictionary = data.reduce(
          (prev, current) => ({
            ...prev,
            [current.id]: `${current.name} ${current.firstName ? current.firstName : ''}`,
          }),
          {},
        );
        setRelationsDictionary(current => ({ ...(current || {}), ...newRelationsDictionary }));
        const showAssignmentName = hasAssignments();
        const invoiceStreamDictionary = data.reduce((prev, current) => {
          const relationStreams = streamsPerRelation[current.id];
          if (!relationStreams || !relationStreams.length) return prev;
          const relationName = `${current.name} ${current.firstName ? current.firstName : ''}`;
          if (!showAssignmentName && relationStreams.length === 1) {
            const [invoiceStream] = relationStreams;
            prev[invoiceStream.id] = relationName;
          } else {
            relationStreams.forEach(invoiceStream => {
              const name = mapInvoiceStreamName(invoiceStream, intl);
              prev[invoiceStream.id] = `${relationName} - ${name}`;
            });
          }
          return prev;
        }, {});
        setInvoiceStreams(current => ({ ...(current || {}), ...invoiceStreamDictionary }));
      });
    };

    fetchRelations();
  }, [relationIds, intl]);

  useEffect(() => {
    const loadTemplates = () => {
      const templateTypes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
      const queryTemplateTypes = templateTypes.reduce(
        (prev, current) => `${prev}&templateTypes=${current}`,
        '',
      );
      const url = `${config.appUrls.api}/api/documentTemplate/Templates?${queryTemplateTypes}`;
      return api.base.get(url).then(response => {
        const templatesById = response.data.reduce(
          (prev, current) => ({ ...prev, [current.id]: current.name }),
          {},
        );
        setTemplates(templatesById);
      });
    };
    const loadInvoiceSchedules = () =>
      api.base
        .get(`${config.appUrls.api}/api/InvoiceSchedule/Get?includeRemoved=true`)
        .then(response => {
          const schedulesById = response.data.reduce((prev, current) => {
            prev[current.id] = formatListValue({
              id: current.id,
              list: 'invoiceschedule',
              fallback: current.name,
              intl,
            });
            return prev;
          }, {});
          setInvoiceSchedules(schedulesById);
        });
    const loadPriceGrids = () =>
      api.base.get(`${config.appUrls.api}/api/Price/grid`).then(response => {
        const priceGridsById = response.data.reduce((prev, current) => {
          prev[current.id] = current.name;
          return prev;
        }, {});
        setPriceGrids(priceGridsById);
      });
    const loadEmailTemplates = () =>
      api.base.get(`${config.appUrls.api}/api/Template/Company`).then(response => {
        const emailTemplatesById = response.data.reduce((prev, current) => {
          prev[current.id] = current.name;
          return prev;
        }, {});
        setEmailTemplates(emailTemplatesById);
      });
    Promise.all([
      loadTemplates(),
      loadInvoiceSchedules(),
      loadPriceGrids(),
      loadEmailTemplates(),
    ]).finally(() => {
      setLoadingData(false);
    });
  }, [intl]);

  useEffect(() => {
    const loadOwnCompanies = () => {
      const ownCompanies = DataCacheService.getAllCompanies().reduce(
        (prev, current) => ({ ...prev, [current.id]: current.name }),
        {},
      );
      setOwnCompanyIds(ownCompanies);
    };

    const loadSalutations = () => {
      const dataCache = DataCacheService.getDataCache();
      if (!dataCache) return;

      setSalutations(dataCache.salutationObject);
    };

    loadOwnCompanies();
    loadSalutations();
  }, []);

  const loadNextPage = useCallback(
    page => {
      const uri = `${
        config.appUrls.audit
      }/api/Relations/${relationId}?limit=${pageSize}&offset=${page * pageSize}`;
      setLoading(true);
      return api.base.get(uri).then(
        ({ data }) => {
          const reducedData = data.map(logLine => ({
            ...logLine,
            id: logLine.dateTime,
            dateTime: new Date(logLine.dateTime),
            type: logLine.type.toLowerCase(),
          }));
          if (data.length < pageSize) setHasMore(false);
          setLogs([...allLogs, ...reducedData]);
          const allRelationIds = reducedData
            .reduce((result, logLine) => {
              switch (logLine.type) {
                case 'relationlink': {
                  result.push(logLine.oldValue.Id);
                  result.push(logLine.newValue.Id);
                  break;
                }
                case 'relationlinkproperty': {
                  result.push(logLine.oldValue.RelationId);
                  if (logLine.property === 'RepresentedBy') {
                    result.push(logLine.oldValue.Value);
                    result.push(logLine.newValue.Value);
                  }
                  break;
                }
                case 'invoicestreamproperty': {
                  if (logLine.property === 'InvoiceViaStreamId') {
                    result.push(logLine.oldValue.InvoiceRelationId);
                    result.push(logLine.newValue.InvoiceRelationId);
                  }
                  break;
                }
                default:
                  break;
              }
              return result;
            }, [])
            .filter(x => x !== null && typeof x !== 'undefined')
            .filter(onlyUnique);
          setRelationIds(allRelationIds);
          setLoading(false);
          return reducedData;
        },
        error => {
          setLoading(false);
          setHasError(true);
          ToastrService.generalError(intl);
          throw error;
        },
      );
    },
    [relationId, allLogs, intl],
  );

  const groupedLogs = useMemo(() => {
    if (!allLogs || allLogs.length === 0) return [];
    return Object.values(
      allLogs.reduce((groups, log) => {
        const groupKey = dateFormatService.formatDate(log.dateTime);
        if (!groups[groupKey])
          // eslint-disable-next-line no-param-reassign
          groups[groupKey] = {
            key: groupKey,
            label: moment(log.dateTime).format('LL'),
            logs: [],
          };
        groups[groupKey].logs.push(log);
        return groups;
      }, {}),
    );
  }, [allLogs]);

  const renderLogLine = log => {
    const { type } = log;
    switch (type.toLowerCase()) {
      case 'field':
        return (
          <FieldChange
            salutations={salutations}
            ownCompanies={ownCompanyIds}
            log={log}
            relations={relationsDictionary}
            templates={templates}
            emailTemplates={emailTemplates}
            users={usersDictionary}
            priceGrids={priceGrids}
          />
        );
      case 'enum':
        return <EnumChange log={log} />;
      case 'relationlink':
        return <RelationLinkChange log={log} relations={relationsDictionary} />;
      case 'ubosnapshot':
        return <UboLine log={log} />;
      case 'relationlinkproperty':
        return (
          <RelationLinkProperty log={log} users={usersDictionary} relations={relationsDictionary} />
        );
      case 'address':
        return <AddressChange log={log} />;
      case 'addresssetting':
        return <AddressSettingChange log={log} />;
      case 'entitycreated':
        return <RelationCreated />;
      case 'ubosnapshotenteredonline':
        return <UboEnteredOnline log={log} />;
      case 'authorization':
        return <AuthorizationChange log={log} />;
      case 'authorizationproperty':
        return (
          <AuthorizationPropertyChange
            salutations={salutations}
            ownCompanies={ownCompanyIds}
            log={log}
            relations={relationsDictionary}
            templates={templates}
            users={usersDictionary}
          />
        );
      case 'compliance':
        return <Compliance log={log} />;
      case 'bankaccount':
        return <BankAccountChange log={log} />;
      case 'bankaccountproperty':
        return (
          <BankAccountPropertyChange
            log={log}
            users={usersDictionary}
            relations={relationsDictionary}
          />
        );
      case 'invoicestream':
        return <InvoiceStreamChange log={log} />;
      case 'invoicestreamproperty':
        return (
          <InvoiceStreamPropertyChange
            log={log}
            relations={relationsDictionary}
            ownCompanies={ownCompanyIds}
            templates={templates}
            emailTemplates={emailTemplates}
            invoiceSchedules={invoiceSchedules}
            priceGrids={priceGrids}
            invoiceStreams={invoiceStreams}
            users={usersDictionary}
          />
        );
      default:
        return <div />;
    }
  };

  const renderSource = (source, documentType) => {
    if (!source || source === 'Unknown' || source === 'Manual' || source === 0 || source === 1)
      return null;

    const sourceFormatted = () => {
      if (Number.isInteger(source)) {
        return formatListValue({ id: source, list: 'entitysourcetype', intl });
      }

      if (source === 'GenericDocumentRecognition' && documentType) {
        return `${intl.formatMessage(messages.GenericDocumentRecognition)} (${formatListValue({
          id: documentType,
          list: 'documenttype',
          intl,
        })})`;
      }

      return source in messages ? intl.formatMessage(messages[source]) : source;
    };

    return (
      <span>
        {' '}
        via <b>{sourceFormatted()}</b>
      </span>
    );
  };
  const renderLogs = logs => {
    if (!logs) return <div />;
    return logs.map(logLine => (
      <li className="relation-audit-container-item list-group-item" key={logLine.id}>
        <div className="log">{renderLogLine(logLine)}</div>
        <div className="info">
          <FormattedTime value={logLine.dateTime} />
          {usersDictionary[logLine.userId] && (
            <span className="name">{usersDictionary[logLine.userId]}</span>
          )}
          {renderSource(logLine.source, logLine.documentType)}
        </div>
      </li>
    ));
  };

  const renderGroups = () => {
    if (!groupedLogs || groupedLogs.length === 0 || loadingTemplates)
      return loading || loadingTemplates ? null : intl.formatMessage(messages.NoItems);
    return groupedLogs
      .filter(x => x !== null)
      .map(({ key, label, logs }) => (
        <div key={key} className="audit-group">
          <div className="audit-group-label">{label}</div>
          <ul className="audit-group-logs list-group">{renderLogs(logs)}</ul>
        </div>
      ));
  };

  if (hasError) return <div />;
  const isLoading = !!(loading || loadingTemplates);

  return (
    <div className="relation-audit-container">
      <div className="col-md-4 col-lg-3 flex-col margin20B">
        {relation ? <RelationSummary relation={relation} /> : <LoadingAnimation />}
      </div>
      <div className="col-lg-9 col-md-8 flex-col">
        {relation ? (
          <InfiniteScroll
            pageStart={-1}
            loadMore={loadNextPage}
            hasMore={hasMore && !isLoading && !hasError}
            initialLoad
            key={relationId}
            loader={null}
          >
            <SimplePanel article="5434831519506" title={intl.formatMessage(messages.AuditLog)} />
            {renderGroups()}
            {isLoading && <LoadingAnimation />}
          </InfiniteScroll>
        ) : (
          <LoadingAnimation />
        )}
      </div>
    </div>
  );
};

RelationAuditContainer.propTypes = {
  relationId: PropTypes.string.isRequired,
};

export default injectStateService(RelationAuditContainer);
